import { NodeSortAction } from "@iventis/domain-model/model/nodeSortAction";
import { FieldSort } from "@iventis/types";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";

const localStorageKey = "user-preferences";

/**
 * How the relay timetable is read, as the user last left it. Declared here rather than taken from the relay timetable
 * so this package stays a leaf, and every field is optional since one added after the user last stored theirs will not
 * be there. The relay timetable resolves what is missing back to its own defaults.
 */
interface StoredRelayTimetableSettings {
    validateSegments?: boolean;
    groupSegments?: boolean;
    splitDisplayMode?: "grouped" | "intertwined";
    units?: { distance: "meters" | "kilometers" | "feet" | "miles"; speed: "mph" | "km/h" };
}

export const userLocalStorageItems = {
    folderBrowserView: "folder-browser-view" as const,
    mapUnitOfMeasurement: "map-unit-of-measurement" as const,
    folderBrowserSort: "folder-browser-sort" as const,
    analysisHelp: "analysis-help" as const,
    objectDetailsPanel: "object-details-panel" as const,
    exportPreviewPositionMapOverlay: "export-preview-position-map-overlay" as const,
    relayTimetableSettings: "relay-timetable-settings" as const,
};

type UserStorageItems = {
    [userLocalStorageItems.folderBrowserView]: string;
    [userLocalStorageItems.mapUnitOfMeasurement]: UnitOfMeasurement;
    [userLocalStorageItems.folderBrowserSort]: FieldSort<NodeSortAction>;
    [userLocalStorageItems.analysisHelp]: boolean;
    [userLocalStorageItems.objectDetailsPanel]: boolean;
    [userLocalStorageItems.exportPreviewPositionMapOverlay]: boolean;
    [userLocalStorageItems.relayTimetableSettings]: StoredRelayTimetableSettings;
};

function generateUserLocalStorageKey(userId: string) {
    return `${localStorageKey}:${userId}`;
}

/** Gets user preferences, Partial as certain keys could not be set yet */
function getUserPreferences(userId: string): Partial<UserStorageItems> {
    const userPreferenceJson = localStorage.getItem(generateUserLocalStorageKey(userId));
    if (userPreferenceJson == null) {
        return {};
    }
    return JSON.parse(userPreferenceJson);
}

/** Type safe userLocalStorage management. get() calls userLocalStorage.getItem() and set() calls userLocalStorage.setItem() */
export const userLocalStorageManager = {
    get: <T extends keyof UserStorageItems>(key: T, userId: string) => {
        const userPreferences = getUserPreferences(userId);
        return userPreferences?.[key];
    },
    set: <T extends keyof UserStorageItems>(key: T, userId: string, item: UserStorageItems[typeof key]): void => {
        // If user preferences does not exist use blank object
        const userPreferences = getUserPreferences(userId) ?? {};
        userPreferences[key] = item;
        localStorage.setItem(generateUserLocalStorageKey(userId), JSON.stringify(userPreferences));
    },
};
